import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Badge, Spinner, Alert,
  Form, InputGroup, Button, Pagination,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import apiService from '../services/api';

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
  { label: 'الصوتيات', icon: 'bi-mic-fill' },
];

const AudioCard = ({ audio }) => {
  const audioUrl = audio.audio?.url
    ? `https://api.almoosawi.co${audio.audio.url}`
    : null;

  const hijriDate = [audio.DD, audio.MM, audio.YYYY].filter(Boolean).join('/');
  const types   = audio.types?.map((t) => t.Name).filter(Boolean) || [];
  const readers = audio.readers?.map((r) => r.Name).filter(Boolean) || [];
  const tags    = audio.tags?.map((t) => t.Name).filter(Boolean) || [];

  return (
    <Card className="h-100 border-0 shadow-sm audio-card">
      {/* Audio visual header */}
      <div className="audio-thumb-wrap">
        <div className="audio-wave-bg">
          <i className="bi bi-soundwave" style={{ fontSize: '3rem', color: 'rgba(255,255,255,0.6)' }} />
        </div>
        {audio.Duration && (
          <span className="duration-badge">{audio.Duration}</span>
        )}
        {audioUrl && (
          <a
            href={audioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="audio-play-overlay"
            title="تشغيل التسجيل"
          >
            <i className="bi bi-play-circle-fill" />
          </a>
        )}
      </div>

      <Card.Body className="p-3">
        <div className="fw-semibold mb-2 audio-title" title={audio.Title}>
          {audio.Title || '—'}
        </div>

        <div className="d-flex flex-wrap gap-1 mb-2">
          {types.map((t) => (
            <Badge key={t} bg="success" style={{ fontSize: '0.7rem' }}>{t}</Badge>
          ))}
          {tags.map((t) => (
            <Badge key={t} bg="secondary" style={{ fontSize: '0.7rem' }}>{t}</Badge>
          ))}
        </div>

        {readers.length > 0 && (
          <div className="text-muted small mb-1">
            <i className="bi bi-person ms-1" />
            {readers.join('، ')}
          </div>
        )}

        {hijriDate && (
          <div className="text-muted small">
            <i className="bi bi-calendar3 ms-1" />
            {hijriDate} هـ
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const Audios = () => {
  const [audios, setAudios]         = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageCount: 1, total: 0 });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage]             = useState(1);

  const fetchAudios = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getAudios({ page, pageSize: 24, search });
      setAudios(data.data || []);
      setPagination(data.meta?.pagination || { page: 1, pageCount: 1, total: 0 });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'تعذّر تحميل الصوتيات');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchAudios(); }, [fetchAudios]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const buildPages = () => {
    const { page: current, pageCount } = pagination;
    const pages = [];
    const delta = 2;
    for (let i = Math.max(1, current - delta); i <= Math.min(pageCount, current + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <Layout title="الصوتيات" breadcrumbs={breadcrumbs}>
      <Container fluid>

        {/* Toolbar */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <Badge bg="secondary" className="fs-6 px-3 py-2">
            <i className="bi bi-mic-fill ms-1" />
            {pagination.total.toLocaleString('ar-SA')} تسجيل
          </Badge>

          <Form onSubmit={handleSearch} className="d-flex gap-2">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="ابحث بالعنوان..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ minWidth: '220px' }}
              />
              <Button type="submit" variant="success">
                <i className="bi bi-search" />
              </Button>
              {search && (
                <Button
                  variant="outline-secondary"
                  onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                >
                  <i className="bi bi-x" />
                </Button>
              )}
            </InputGroup>
          </Form>
        </div>

        {error && (
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle ms-2" />{error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="success" />
          </div>
        ) : audios.length === 0 ? (
          <Alert variant="info" className="text-center">
            <i className="bi bi-info-circle ms-2" />
            لا توجد نتائج{search ? ` للبحث عن "${search}"` : ''}.
          </Alert>
        ) : (
          <>
            <Row className="g-3">
              {audios.map((audio) => (
                <Col xs={12} sm={6} md={4} xl={3} key={audio.documentId}>
                  <AudioCard audio={audio} />
                </Col>
              ))}
            </Row>

            {pagination.pageCount > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                  <Pagination.Prev onClick={() => setPage((p) => p - 1)} disabled={page === 1} />
                  {buildPages().map((p) => (
                    <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                      {p}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => setPage((p) => p + 1)} disabled={page === pagination.pageCount} />
                  <Pagination.Last onClick={() => setPage(pagination.pageCount)} disabled={page === pagination.pageCount} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Container>

      <style>{`
        .audio-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          overflow: hidden;
        }
        .audio-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 0.5rem 1.2rem rgba(0,0,0,0.12) !important;
        }
        .audio-thumb-wrap {
          position: relative;
          aspect-ratio: 16/9;
          overflow: hidden;
        }
        .audio-wave-bg {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #198754 0%, #146c43 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .duration-badge {
          position: absolute;
          bottom: 6px;
          left: 6px;
          background: rgba(0,0,0,0.6);
          color: #fff;
          font-size: 0.72rem;
          padding: 1px 5px;
          border-radius: 3px;
        }
        .audio-play-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0);
          font-size: 3rem;
          transition: color 0.2s;
          text-decoration: none;
        }
        .audio-card:hover .audio-play-overlay { color: rgba(255,255,255,0.9); }
        .audio-title {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          font-size: 0.9rem;
          line-height: 1.4;
          min-height: 2.5rem;
        }
      `}</style>
    </Layout>
  );
};

export default Audios;
